import React, { useState, useEffect } from 'react';
import { Newspaper, CircleChevronRight, ChevronRight, ChevronLeft } from 'lucide-react';

export const NewsSection = () => {
  const [newsData, setNewsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 앱 환경 감지 및 링크 처리 함수
  const handleExternalLink = (url) => {
    // Android WebView에서 JavaScript Interface 확인
    if (window.Android && window.Android.openExternalUrl) {
      window.Android.openExternalUrl(url);
      return;
    }
    
    // iOS WebView에서 JavaScript Interface 확인
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.openExternalUrl) {
      window.webkit.messageHandlers.openExternalUrl.postMessage(url);
      return;
    }
    
    // React Native WebView에서 postMessage 확인
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'OPEN_EXTERNAL_URL',
        url: url
      }));
      return;
    }
    
    // 모바일 환경에서 외부 앱으로 열기
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    if (isMobile) {
      // 모바일에서는 같은 탭에서 열기 (앱 내 브라우저)
      window.location.href = url;
    } else {
      // 데스크톱에서는 새 탭에서 열기
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // 대시보드 뉴스 API 호출
  const fetchDashboardNews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch('https://mmstudio.co.kr:10444/api/v1/open/dashboard/dashboard-news-data', {
        method: 'GET',
        headers: {
          'accept': '*/*'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch dashboard news');
      }
      
      const data = await response.json();
      setNewsData(data.list || []);
    } catch (error) {
      console.error('Error fetching dashboard news:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // 컴포넌트 마운트 시 API 호출
  useEffect(() => {
    fetchDashboardNews();
  }, []);

  // 시간 계산 함수
  const getTimeAgo = (publishedDate) => {
    const now = new Date();
    const published = new Date(publishedDate);
    const diffInHours = Math.floor((now - published) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return '방금 전';
    if (diffInHours < 24) return `${diffInHours}시간 전`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}일 전`;
    
    return published.toLocaleDateString('ko-KR');
  };

  // 감성 분석 결과를 태그로 변환
  const getSentimentTags = (sentiment, severity, keywords_ko) => {
    const tags = [];
    
    // 감성 태그 추가
    if (sentiment) {
      tags.push({
        text: sentiment === 'positive' ? '긍정적' : sentiment === 'negative' ? '부정적' : '중립적',
        type: sentiment
      });
    }
    
    // 심각도 태그 추가
    if (severity) {
      tags.push({
        text: severity === 'CRITICAL' ? '중요' : severity,
        type: severity === 'CRITICAL' ? 'negative' : 'neutral'
      });
    }
    
    // 키워드 태그 추가 (최대 2개)
    if (keywords_ko && keywords_ko.length > 0) {
      keywords_ko.slice(0, 2).forEach(keyword => {
        if (keyword && keyword.trim()) {
          tags.push({
            text: keyword,
            type: 'neutral'
          });
        }
      });
    }
    
    return tags;
  };

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 3; // 한 페이지에 3개씩 표시
  const totalPages = Math.ceil(newsData.length / itemsPerPage);

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return newsData.slice(startIndex, endIndex);
  };

  const generatePageNumbers = (current, total) => {
    const pages = [];
    if (total <= 7) {
      for (let i = 1; i <= total; i++) pages.push(i);
    } else if (current <= 4) {
      pages.push(1, 2, 3, 4, 5, '...', total);
    } else if (current >= total - 3) {
      pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
    } else {
      pages.push(1, '...', current - 1, current, current + 1, '...', total);
    }
    return pages;
  };

  const getTagStyle = (type) => {
    switch (type) {
      case 'positive':
        return 'bg-green-50 text-green-600 border border-green-100';
      case 'negative':
        return 'bg-red-50 text-red-600 border border-red-100';
      case 'neutral':
      default:
        return 'bg-[rgba(184,167,232,0.12)] text-[#7c6db5] border border-[rgba(184,167,232,0.2)]';
    }
  };

  return (
    <div className="feature-section">

  <div className="flex items-center gap-1 mb-2">
    <div className="feature-icon">
      <Newspaper size={20} />
    </div>
    <h2 className="text-xm font-bold text-black">최신 뉴스</h2>
  </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        실시간 시장 뉴스와 기업 발표를 확인하여 투자 기회를 놓치지 마세요.
      </p>
      
      {/* 뉴스 아이템들 */}
      <div className="space-y-4 mb-6">
        {loading ? (
          // 로딩 상태
          Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="card animate-pulse">
              <div className="h-4 bg-gray-200 rounded mb-1 w-32"></div>
              <div className="h-4 bg-gray-200 rounded mb-1"></div>
              <div className="h-3 bg-gray-200 rounded mb-2 w-full"></div>
              <div className="h-3 bg-gray-200 rounded mb-2 w-3/4"></div>
              <div className="flex justify-between">
                <div className="flex space-x-1">
                  <div className="h-6 bg-gray-200 rounded w-16"></div>
                  <div className="h-6 bg-gray-200 rounded w-12"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-20"></div>
              </div>
            </div>
          ))
        ) : error ? (
          // 에러 상태
          <div className="card">
            <div className="text-center py-8">
              <div className="text-red-600 font-medium mb-2">뉴스를 불러오는 중 오류가 발생했습니다</div>
              <button
                onClick={fetchDashboardNews}
                className="px-4 py-2 bg-[#B8A7E8] text-white rounded-lg hover:bg-[#9b8ce8] transition-colors text-sm font-medium"
              >
                다시 시도
              </button>
            </div>
          </div>
        ) : newsData.length === 0 ? (
          // 데이터 없음 상태
          <div className="card">
            <div className="text-center py-8">
              <div className="text-gray-600">표시할 뉴스가 없습니다</div>
            </div>
          </div>
        ) : (
          // 정상 데이터 표시
          getCurrentPageData().map((news) => {
            const tags = getSentimentTags(news.sentiment, news.severity, news.keywords_ko);
            return (
              <div key={news.hash_id} className="card cursor-pointer" onClick={() => handleExternalLink(news.url)}>
                <div className="flex items-center gap-1.5 mb-1.5">
                  <span className="text-xs text-blue-600 font-semibold">{news.source}</span>
                  <span className="text-gray-300 text-xs">·</span>
                  <span className="text-xs text-gray-400">{getTimeAgo(news.published_date)}</span>
                </div>
                <h3 className="text-sm font-bold mb-1 leading-snug">
                  {news.title_ko || news.title}
                </h3>
                <p className="text-xs text-gray-500 mb-2.5 leading-relaxed">
                  {news.summary_ko || news.summary}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex flex-wrap gap-1">
                    {tags.map((tag, index) => (
                      <span
                        key={index}
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${getTagStyle(tag.type)}`}
                      >
                        {tag.text}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleExternalLink(news.url);
                    }}
                    className="text-blue-400 flex items-center hover:text-blue-600 hover:scale-110 transition-all cursor-pointer bg-transparent border-none p-0 flex-shrink-0 ml-2"
                  >
                    <CircleChevronRight size={18} />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* 페이지네이션 */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 transition-all text-gray-500"
          >
            <ChevronLeft size={14} />
          </button>

          <div className="flex gap-0.5">
            {generatePageNumbers(currentPage, totalPages).map((page, index) => {
              if (page === '...') {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="w-8 h-8 flex items-center justify-center text-xs text-gray-300"
                  >
                    ···
                  </span>
                );
              }
              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`w-8 h-8 rounded-lg text-xs font-semibold transition-all ${
                    currentPage === page
                      ? 'bg-[#B8A7E8] text-white shadow-sm shadow-[rgba(184,167,232,0.4)]'
                      : 'text-gray-400 hover:bg-gray-50 hover:text-gray-600'
                  }`}
                >
                  {page}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 hover:border-gray-300 transition-all text-gray-500"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default NewsSection;
